import React from 'react';
import { clsx } from 'clsx';

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', className, disabled, loading, onClick, type = 'button', ...rest }) {
  const base = 'inline-flex items-center gap-1.5 font-medium transition-all duration-150 border cursor-pointer select-none disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary:   'bg-accent text-white border-transparent hover:bg-accent-hover',
    secondary: 'bg-transparent text-secondary border-border hover:bg-secondary hover:text-primary',
    danger:    'bg-transparent text-danger border-danger-border hover:bg-danger-bg',
    ghost:     'bg-transparent border-transparent text-secondary hover:bg-secondary',
  };
  const sizes = {
    sm: 'px-2.5 py-1.5 text-xs rounded-md',
    md: 'px-3.5 py-2 text-sm rounded-md',
    lg: 'px-5 py-2.5 text-sm rounded-lg',
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      style={{ borderRadius: 'var(--radius-md)', fontSize: size === 'lg' ? '14px' : '13px' }}
      className={className}
      {...rest}
      css={undefined}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: size === 'lg' ? '8px 18px' : size === 'sm' ? '4px 10px' : '6px 14px',
        background: variant === 'primary' ? 'var(--accent)' : 'transparent',
        color: variant === 'primary' ? '#fff' : variant === 'danger' ? 'var(--danger)' : 'var(--text-secondary)',
        border: variant === 'primary' ? 'none' : `0.5px solid ${variant === 'danger' ? 'var(--danger)' : 'var(--border-med)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.6 : 1,
        fontFamily: 'var(--font-sans)',
        fontWeight: 500,
        transition: 'all .15s',
        whiteSpace: 'nowrap',
      }}
      onMouseOver={e => {
        if (!disabled && !loading) {
          if (variant === 'primary') e.currentTarget.style.background = 'var(--accent-hover)';
          else e.currentTarget.style.background = 'var(--bg-secondary)';
        }
      }}
      onMouseOut={e => {
        if (variant === 'primary') e.currentTarget.style.background = 'var(--accent)';
        else e.currentTarget.style.background = 'transparent';
      }}
      >
        {loading && <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />}
        {children}
      </span>
    </button>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, className, style, ...rest }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '0.5px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '20px',
      ...style,
    }} {...rest}>
      {children}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ children, variant = 'neutral', style }) {
  const variants = {
    neutral: { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' },
    success: { background: 'var(--success-bg)',   color: 'var(--success-text)'   },
    warning: { background: 'var(--warning-bg)',   color: 'var(--warning-text)'   },
    danger:  { background: 'var(--danger-bg)',    color: 'var(--danger-text)'    },
    info:    { background: 'var(--info-bg)',       color: 'var(--info-text)'      },
    accent:  { background: 'var(--accent-light)', color: 'var(--accent)'         },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
      ...variants[variant], ...style,
    }}>
      {children}
    </span>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
export function Input({ label, hint, error, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</label>}
      <input
        style={{
          padding: '8px 10px',
          border: `0.5px solid ${error ? 'var(--danger)' : 'var(--border-med)'}`,
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          fontSize: 13,
          outline: 'none',
          fontFamily: 'var(--font-sans)',
          transition: 'border .15s',
          width: '100%',
        }}
        onFocus={e => e.target.style.borderColor = 'var(--accent)'}
        onBlur={e  => e.target.style.borderColor = error ? 'var(--danger)' : 'var(--border-med)'}
        {...props}
      />
      {hint  && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{hint}</span>}
      {error && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{error}</span>}
    </div>
  );
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ label, children, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</label>}
      <select
        style={{
          padding: '8px 10px',
          border: '0.5px solid var(--border-med)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          fontSize: 13,
          width: '100%',
          fontFamily: 'var(--font-sans)',
          cursor: 'pointer',
        }}
        {...props}
      >
        {children}
      </select>
    </div>
  );
}

// ── Metric Card ───────────────────────────────────────────────────────────────
export function MetricCard({ label, value, trend, trendUp, barWidth, barColor }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', padding: '16px 18px' }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      {trend && (
        <div style={{ fontSize: 11, marginTop: 5, color: trendUp !== false ? 'var(--success)' : 'var(--danger)' }}>
          {trendUp !== false ? '↑' : '↓'} {trend}
        </div>
      )}
      {barWidth !== undefined && (
        <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${barWidth}%`, background: barColor || 'var(--accent)', borderRadius: 2, transition: 'width .8s ease' }} />
        </div>
      )}
    </div>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────────
export function ProgressBar({ percent, color, height = 10, animated = true }) {
  return (
    <div style={{ height, background: 'var(--border)', borderRadius: height / 2, overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: `${Math.min(percent, 100)}%`,
        background: color || 'linear-gradient(90deg, var(--accent), var(--accent2))',
        borderRadius: height / 2,
        transition: animated ? 'width .5s ease' : 'none',
      }} />
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 24, color }) {
  return (
    <div style={{
      width: size, height: size,
      border: `${size > 20 ? 3 : 2}px solid var(--border)`,
      borderTopColor: color || 'var(--accent)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      flexShrink: 0,
    }} />
  );
}

// ── Section Header ────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>}
    </div>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────
export function Table({ columns, data, onRowClick }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th key={i} style={{
                textAlign: 'left', padding: '8px 12px',
                fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)',
                borderBottom: '0.5px solid var(--border)',
                textTransform: 'uppercase', letterSpacing: '.04em',
              }}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr
              key={ri}
              onClick={() => onRowClick?.(row)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            >
              {columns.map((col, ci) => (
                <td key={ci} style={{ padding: '10px 12px', borderBottom: '0.5px solid var(--border)', color: 'var(--text-primary)' }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
          {data.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Alert ─────────────────────────────────────────────────────────────────────
export function Alert({ variant = 'info', title, children }) {
  const vars = {
    info:    { bg: 'var(--info-bg)',    color: 'var(--info-text)',    icon: 'ℹ' },
    success: { bg: 'var(--success-bg)', color: 'var(--success-text)', icon: '✓' },
    warning: { bg: 'var(--warning-bg)', color: 'var(--warning-text)', icon: '⚠' },
    danger:  { bg: 'var(--danger-bg)',  color: 'var(--danger-text)',  icon: '✗' },
  };
  const v = vars[variant] || vars.info;
  return (
    <div style={{ padding: '12px 16px', background: v.bg, borderRadius: 'var(--radius-md)', border: `0.5px solid ${v.color}22` }}>
      {title && <div style={{ fontSize: 13, fontWeight: 500, color: v.color, marginBottom: 4 }}>{v.icon} {title}</div>}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{children}</div>
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────
export function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 32, height: 18,
          background: checked ? 'var(--accent)' : 'var(--border-med)',
          borderRadius: 9, position: 'relative', transition: 'background .2s',
          flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 2, left: checked ? 14 : 2,
          width: 14, height: 14, background: '#fff',
          borderRadius: '50%', transition: 'left .2s',
          boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        }} />
      </div>
      {label && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>}
    </label>
  );
}

// ── Tab Bar ───────────────────────────────────────────────────────────────────
export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: '0.5px solid var(--border)', marginBottom: 16 }}>
      {tabs.map(tab => (
        <div
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            padding: '8px 16px', fontSize: 13, cursor: 'pointer',
            borderBottom: active === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -0.5,
            color: active === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
            fontWeight: active === tab.id ? 500 : 400,
            transition: 'all .15s',
          }}
        >
          {tab.label}
        </div>
      ))}
    </div>
  );
}
